import { SEO } from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, CheckCircle2, Wrench, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SITE, breadcrumbLd } from "@/lib/seo-config";

const PAGE_URL = `${SITE.url}/changelog`;
const VERSION = "1.6.0";
const RELEASE_DATE = "2026-06-06"; // ISO for schema.org

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "How do I update FitAura AI on Android?",
    a: "Open the Google Play Store, search for FitAura AI, and tap Update. Most users get updates automatically — the latest version installs the next time you open the app since FitAura AI uses over-the-air delivery for web-layer changes.",
  },
  {
    q: "Do I have to watch ads to use FitAura AI?",
    a: "No. Watching a rewarded ad is always optional and user-initiated — you tap a 'Watch ad' button yourself. Free users can use their starter credits without any ad. Rewarded ads only appear when you want to unlock extra free generations or continue scrolling the Featured feed past the 20-look batch.",
  },
  {
    q: "How do I remove ads from FitAura AI?",
    a: "Any paid credit purchase turns off all third-party ads automatically. Pro users (anyone who has bought credits) see only first-party house cards in the Featured feed — no rewarded video, no interstitials, no AdMob tracking.",
  },
  {
    q: "Where are my generated try-on images stored?",
    a: "Generated try-on and outfit images live in our private cloud storage for 24 hours and are then permanently deleted by an hourly automated sweeper. On Android, results are auto-saved to your device gallery so you keep them after the cloud copy expires. Submissions to the Featured feed are kept permanently until you remove them.",
  },
];

const softwareAppLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  operatingSystem: "Android 8.0+, iOS, Web",
  applicationCategory: "LifestyleApplication",
  softwareVersion: VERSION,
  datePublished: RELEASE_DATE,
  releaseNotes:
    "Faster Featured feed with native offline image caching, optional rewarded video ads for free users every 20 looks, cleaner ad-light experience for Pro members with first-party house cards, Google Sign-In fix on Android, offline-mode stability fixes, and confirmed 24-hour auto-deletion of generated images.",
  url: PAGE_URL,
  image: SITE.defaultOgImage,
  publisher: {
    "@type": "Organization",
    name: SITE.name,
    url: SITE.url,
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free with optional paid credit packs",
  },
};

const faqPageLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const crumbsLd = breadcrumbLd([
  { name: "Home", url: SITE.url },
  { name: "Changelog", url: PAGE_URL },
]);

const Changelog = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="FitAura AI Changelog — Latest Updates & Release Notes"
        description="Official FitAura AI release notes — new features, Android updates, and fixes for our AI virtual try-on app. See what's new and how to update."
        keywords="FitAura AI changelog, FitAura AI release notes, FitAura AI updates, AI virtual try-on update, FitAura Android update"
        canonical="/changelog"
        jsonLd={[softwareAppLd, faqPageLd, crumbsLd]}
      />
      <div className="relative min-h-screen bg-background text-foreground">
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
          <div className="absolute right-0 top-40 h-[320px] w-[320px] rounded-full bg-accent/10 blur-[120px]" />
        </div>

        <header className="sticky top-0 z-30 border-b border-white/5 bg-background/85 px-5 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
          <div className="mx-auto flex w-full max-w-2xl items-center gap-2.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              aria-label="Back"
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
              <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
            </span>
            <div className="min-w-0">
              <span className="font-display block text-[15px] font-bold leading-none tracking-tight">
                FitAura<span className="text-primary"> AI</span>
              </span>
              <span className="font-display mt-1 block truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Changelog
              </span>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-2xl px-5 py-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_0_24px_-6px_hsl(var(--primary))]">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
                — RELEASE NOTES
              </span>
              <h1 className="font-display mt-0.5 text-2xl font-bold uppercase tracking-tight">
                FitAura AI Changelog
              </h1>
              <p className="text-xs text-muted-foreground">
                What's new in the AI virtual try-on app, fix-by-fix.
              </p>
            </div>
          </div>

          {/* v1.6 entry */}
          <article>
            <Card className="space-y-5 p-5 shadow-soft">
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-3">
                <h2 className="font-display text-lg font-bold tracking-tight">
                  v{VERSION} — Faster feed, smarter ads
                </h2>
                <time
                  dateTime={RELEASE_DATE}
                  className="text-xs font-medium text-muted-foreground"
                >
                  June 6, 2026
                </time>
              </header>

              <section>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-primary">
                  TL;DR
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  The Featured feed is dramatically faster on Android thanks to native image
                  caching. Free users can now unlock the next 20 looks by watching one optional
                  rewarded ad — and Pro users get a cleaner, ad-light feed with only first-party
                  house cards. Plus a Google Sign-In fix and crash fixes across the app.
                </p>
              </section>

              <section>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-foreground">
                  <Sparkles className="h-4 w-4 text-primary" /> New features
                </h3>
                <ul className="space-y-2 pl-1 text-sm leading-relaxed text-muted-foreground">
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <span className="font-semibold text-foreground">Faster Featured &amp; Top Featured feed</span> — images now
                      cache locally on Android, so scrolling stays smooth even on flaky connections.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <span className="font-semibold text-foreground">Rewarded ad gate for free users</span> — tap
                      "Watch ad to continue" once every 20 looks to keep scrolling. Always optional, never auto-played.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <span className="font-semibold text-foreground">Ad-light feed for Pro users</span> — anyone
                      who has purchased credits sees zero third-party ads. Just scrollable first-party house cards
                      every 20th tile.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <span className="font-semibold text-foreground">24-hour cloud retention confirmed</span> —
                      generated try-on images now auto-save to your Android gallery and are deleted from the cloud
                      after 24 hours by an hourly sweeper.
                    </span>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-foreground">
                  <Wrench className="h-4 w-4 text-primary" /> Fixes &amp; performance
                </h3>
                <ul className="space-y-2 pl-1 text-sm leading-relaxed text-muted-foreground">
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Google Sign-In on Android no longer fails with "Unsupported provider".</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Offline-mode banner no longer flashes on flaky connections.</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Reduced cold-start time on Android by deferring non-critical scripts.</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Stability fixes in the AuraScore and Result screens.</span>
                  </li>
                </ul>
              </section>

              <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary">
                  <Smartphone className="h-4 w-4" /> How to update on Android
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Most users get this update automatically the next time they open the app — FitAura AI
                  uses over-the-air delivery for web-layer changes. To force-update the native shell,
                  open the{" "}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.fitauraai.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Google Play Store listing
                  </a>{" "}
                  and tap <span className="font-semibold text-foreground">Update</span>.
                </p>
              </section>
            </Card>
          </article>

          {/* FAQ block — also emitted as FAQPage JSON-LD above */}
          <section className="mt-8">
            <h2 className="font-display mb-3 text-lg font-bold tracking-tight">
              Frequently asked questions
            </h2>
            <Card className="divide-y divide-border/60 p-0 shadow-soft">
              {FAQS.map((f) => (
                <details key={f.q} className="group p-4">
                  <summary className="cursor-pointer list-none text-sm font-semibold text-foreground transition-colors hover:text-primary">
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </Card>
          </section>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            <Link to="/" className="underline-offset-2 hover:underline">Home</Link>
            {" · "}
            <Link to="/about" className="underline-offset-2 hover:underline">About</Link>
            {" · "}
            <Link to="/privacy" className="underline-offset-2 hover:underline">Privacy</Link>
            {" · "}
            <Link to="/terms" className="underline-offset-2 hover:underline">Terms</Link>
            {" · "}
            <Link to="/refund" className="underline-offset-2 hover:underline">Refund</Link>
          </p>
        </main>
      </div>
    </>
  );
};

export default Changelog;
